import { Component, inject, OnInit } from '@angular/core';
import { AuthService } from "@core/services/auth.service";
import { OrganisationModel } from "@models/organisations/organisation-model";
import { OrganisationSettingData } from '@models/settings/organisation-setting-data';
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import { AppCopyDirective } from "@shared/directives/copy.directive";
import { HasRoleDirective } from '@shared/directives/has-role.directive';
import { DefaulValuePipe } from "@shared/pipes/defaultValuePipe";
import { PhonePipe } from "@shared/pipes/phonePipe";
import { IPartialHeaderData } from "@shared/services/header.service";
import { OrganisationApiService } from '@shared/services/organisation-api.service';
import { SettingsApiService } from '@shared/services/settings-api.service';
import { compare } from 'fast-json-patch';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, finalize } from 'rxjs';
import { ModalFormsService } from '../../../../forms/services/modal-forms.service';
import {
  AlreadyRegisteredDomainModalComponent
} from './components/already-registered-domain-modal/already-registered-domain-modal.component';
import {
  EditOrganisationProfileModalComponent
} from "./components/edit-organisation-profile-modal/edit-organisation-profile-modal.component";

@Component({
  selector: 'app-organisation-details-page',
  standalone: true,
  templateUrl: './organisation-details-page.component.html',
  styleUrls: ['./organisation-details-page.component.scss', '../../styles/settings-styles.scss'],
  imports: [
    AppCopyDirective,
    DefaulValuePipe,
    LoaderComponent,
    PhonePipe,
    HasRoleDirective
  ],
})
export class OrganisationDetailsPageComponent extends CommonPageComponent implements OnInit {
  private organisationService = inject(OrganisationApiService);
  private authService = inject(AuthService);
  private modalFormsService = inject(ModalFormsService);
  private modalService = inject(NgbModal);
  private settingsService = inject(SettingsApiService);
  private toastr = inject(ToastrService);

  override readonly headerData: IPartialHeaderData = {
    title: 'Organisation Details',
    iconClass: 'ic-organisation-20',
    backPath: ['/settings']
  };

  organisation!: OrganisationModel;
  editOrganisationModalOpened = true;
  primaryContactLabel: string | null = null;
  billingContactLabel: string | null = null;

  organisationSettings: OrganisationSettingData | null = null;

  openAlreadyRegisteredDomainModal(): void {
    this.modalFormsService.openModal(AlreadyRegisteredDomainModalComponent,
      {
        size: 'sm',
        centered: true,
        backdrop: 'static',
        keyboard: false
      });
  }

  openEditOrganisationModal(): void {
    if (this.editOrganisationModalOpened || this.isLoading()) return;
    this.editOrganisationModalOpened = true;
    const modalRef = this.modalService.open(EditOrganisationProfileModalComponent, { size: 'lg' });
    const instance = modalRef.componentInstance as EditOrganisationProfileModalComponent;
    instance.organisation = structuredClone(this.organisation);
    this.editOrganisationModalOpened = true;

    modalRef.result.then(
      () => {
        this.authService.updateUserProfile().subscribe();
        this.loadOrganisation();
      },
      () => {
      }
    ).finally(() => this.editOrganisationModalOpened = false);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadOrganisation();
    //this.loadOrganisationSettings();
  }

  // loadOrganisationSettings(){
  //   if(!this.authService.isSuperuser)
  //     return;

  //   this.settingsService.getOrganisationSettings().subscribe({
  //     next: (settings) => {
  //       this.organisationSettings = settings;
  //     }
  //   });
  // }

  loadOrganisation() {
    this.isLoading.set(true);
    combineLatest([
      this.organisationService.getOrganisationProfile(),
      this.organisationService.getOrganisation()
    ])
      .pipe(finalize(() => {
        this.isLoading.set(false);
        if (this.organisationSetupRequired) {
          this.headerService.patchHeaderData({ backPath: this.organisationSetupRequired ? null : this.headerData.backPath });
          if (this.isAlreadyRegisteredDomain) {
            this.openAlreadyRegisteredDomainModal();
          } else {
            this.openEditOrganisationModal();
          }
        }
      }))
      .subscribe(([profile, organisation]) => {
        this.organisation = organisation;
        this.billingContactLabel = organisation.billingContactId
          ? profile.users?.find(u => u.id === organisation.billingContactId)?.fullName ?? null
          : null;
        this.primaryContactLabel = organisation.primaryContactId
          ? profile.users?.find(u => u.id === organisation.primaryContactId)?.fullName ?? null
          : null;

        this.editOrganisationModalOpened = false;
      });
  }

  //tmp solution to save time
  // changeVerificationEnabledValue() {
  //   const newValue = new OrganisationSettingData({
  //     individualVerificationEnabled: !this.organisationSettings?.individualVerificationEnabled
  //   });

  //   const orgSettingsPatch = compare(this.organisationSettings!, newValue);

  //   this.settingsService.patchOrganisationSettings(orgSettingsPatch).subscribe({
  //     next: (settings) => {
  //       this.organisationSettings = settings;
  //       this.toastr.success('Organisation settings updated successfully');
  //     },
  //     error: () => {
  //       this.toastr.error('Failed to update organisation settings');
  //     }
  //   });
  // }

  get organisationSetupRequired(): boolean {
    return this.authService.currentUserProfile()?.organisationSetupRequired ?? false;
  }

  get isAlreadyRegisteredDomain(): boolean {
    return this.authService.currentUserProfile()?.isAlreadyRegisteredDomain ?? false;
  }
}
